/**
 * OverviewTab - Dataset overview with details and Andes status
 * @module components/starnexus/datasets/tabs/OverviewTab
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';
import KeyValuePairs from '@amzn/awsui-components-console/key-value-pairs';
import Link from '@amzn/awsui-components-console/link';
import Alert from '@amzn/awsui-components-console/alert';
import Table from '@amzn/awsui-components-console/table';
import Spinner from '@amzn/awsui-components-console/spinner';
import type { Dataset } from '../../../../types/starnexus';
import type { OrchestrationStepName } from '../../../../types/starnexus';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../../utils/apiClient';

interface OverviewTabProps {
  dataset: Dataset;
}

/** Button that triggers POST /orchestration/:id/step/all to advance pending steps. */
const ContinuePipelineButton: React.FC<{ datasetId: string; orchestration: any }> = ({ datasetId, orchestration }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const hasPending = Object.values(orchestration || {}).some(
    (s: any) => s?.status === 'pending' || s?.status === 'failed'
  );
  if (!hasPending) return null;

  const handleContinue = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await apiFetch(apiUrl(`/api/orchestration/${encodeURIComponent(datasetId)}/step/all`), {
        ...apiFetchDefaults,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok) {
        setResult(`✅ Pipeline advanced: ${(data.orchestrated || []).join(', ')}`);
      } else {
        setResult(`❌ ${data.error || 'Failed'}`);
      }
    } catch (e: any) {
      setResult(`❌ ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SpaceBetween size="xs" direction="horizontal">
      <Button variant="primary" loading={loading} onClick={handleContinue}>
        Continue pipeline
      </Button>
      {result && <Box variant="small">{result}</Box>}
    </SpaceBetween>
  );
};

interface PreviewData {
  columns: string[];
  rows: Record<string, unknown>[];
  sampledFrom: { snapshotDate: string; key: string } | null;
  message?: string;
}

const formatDate = (date: Date): string => {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const ORCHESTRATION_STEPS: OrchestrationStepName[] = ['ddb', 'glue', 'transform', 'andes', 'datacraft'];

export const OverviewTab: React.FC<OverviewTabProps> = ({ dataset }) => {
  const navigate = useNavigate();
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (!dataset.datasetId) return;
    // Skip preview fetch for datasets that have no data yet.
    if (dataset.status === 'draft' || dataset.status === 'andes_deleted') return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    apiFetch(apiUrl(`/api/datasets/${encodeURIComponent(dataset.datasetId)}/preview`), { ...apiFetchDefaults })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data.error) {
          setPreviewError(data.message || data.error);
        } else {
          setPreview(data as PreviewData);
        }
      })
      .catch(e => { if (!cancelled) setPreviewError(e.message); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [dataset.datasetId, dataset.status]);

  return (
    <SpaceBetween size="l">
      {/* Creation incomplete banner */}
      {(dataset.status === 'failed' || dataset.status === 'draft' || (dataset.status === 'processing' && dataset.orchestration && Object.values(dataset.orchestration).some((s: any) => s?.status === 'pending' || s?.status === 'failed'))) && (
        <Container header={<Header variant="h2" actions={<Button variant="primary" onClick={() => navigate(`/starnexus/uploader/datasets/create?resume=${dataset.datasetId}`)}>Resume creation</Button>}>Creation incomplete</Header>}>
          <Alert type="warning">
            This dataset was not fully created. Some AWS resources may be missing. Click &apos;Continue pipeline&apos; to resume.
          </Alert>
          {dataset.orchestration && (
            <SpaceBetween size="s">
              {ORCHESTRATION_STEPS.map((stepKey) => {
                const step = dataset.orchestration?.[stepKey];
                if (!step) return null;
                return (
                  <Box key={stepKey}>
                    <StatusIndicator type={step.status === 'success' ? 'success' : step.status === 'failed' ? 'error' : step.status === 'in_progress' ? 'in-progress' : 'pending'}>
                      {stepKey}: {step.status}
                    </StatusIndicator>
                    {step.error && <Box color="text-status-error">{step.error}</Box>}
                  </Box>
                );
              })}
              <ContinuePipelineButton datasetId={dataset.datasetId} orchestration={dataset.orchestration} />
            </SpaceBetween>
          )}
        </Container>
      )}

      {/* Dataset Details */}
      <Container header={<Header variant="h2">Dataset details</Header>}>
        <KeyValuePairs
          columns={3}
          items={[
            { label: 'Dataset name', value: dataset.datasetName || '-' },
            { label: 'Status', value: dataset.status ? <StatusIndicator type={dataset.status === 'active' ? 'success' : 'warning'}>{dataset.status}</StatusIndicator> : '-' },
            { label: 'Processing type', value: dataset.processingType || '-' },
            { label: 'Created', value: dataset.createdAt ? formatDate(dataset.createdAt) : '-' },
            { label: 'Last processed', value: dataset.stats?.lastActivity ? formatDate(dataset.stats.lastActivity) : '-' },
            { label: 'Success rate', value: dataset.stats?.successRate ? `${dataset.stats.successRate.toFixed(1)}%` : '-' },
          ]}
        />
      </Container>

      {/* AWS Resources — Glue, S3, orchestration step details */}
      {dataset.status !== 'draft' && (
        <Container header={<Header variant="h2">AWS resources</Header>}>
          <KeyValuePairs
            columns={2}
            items={[
              { label: 'Glue database', value: dataset.glueDatabase || 'starnexus' },
              { label: 'Glue table', value: dataset.glueTableName ? (
                <Link external href={`https://us-east-1.console.aws.amazon.com/glue/home?region=us-east-1#/v2/data-catalog/tables/view/${dataset.glueTableName}?database=starnexus`}>
                  {dataset.glueTableName}
                </Link>
              ) : '-' },
              { label: 'S3 prefix', value: dataset.s3Prefix ? (
                <Link external href={`https://s3.console.aws.amazon.com/s3/buckets/${dataset.s3Prefix?.split('/')[0] || ''}?prefix=${dataset.s3Prefix || ''}`}>
                  {dataset.s3Prefix}
                </Link>
              ) : '-' },

            ]}
          />
          {dataset.orchestration && (
            <Box margin={{ top: 'm' }}>
              <Header variant="h3">Orchestration steps</Header>
              <SpaceBetween size="xs">
                {ORCHESTRATION_STEPS.map((stepKey) => {
                  const step = dataset.orchestration?.[stepKey];
                  if (!step) return null;
                  const statusType = step.status === 'success' ? 'success' : step.status === 'failed' ? 'error' : step.status === 'in_progress' ? 'in-progress' : 'pending';
                  return (
                    <Box key={stepKey}>
                      <StatusIndicator type={statusType}>
                        {stepKey}: {step.status}
                        {step.meta?.jobRunId && <> — Job: <code>{step.meta.jobRunId}</code></>}
                        {step.meta?.tableArn && <> — ARN: <code>{step.meta.tableArn}</code></>}
                        {step.meta?.skipped && ' (skipped — already existed)'}
                      </StatusIndicator>
                      {step.error && <Box color="text-status-error" fontSize="body-s">{step.error}</Box>}
                      {step.completedAt && <Box color="text-body-secondary" fontSize="body-s">Completed: {formatDate(new Date(step.completedAt))}</Box>}
                    </Box>
                  );
                })}
              </SpaceBetween>
            </Box>
          )}
        </Container>
      )}

      {/* Andes Status */}
      <Container header={<Header variant="h3">Andes table</Header>}>
        {dataset.status === 'andes_deleted' ? (
          <Alert type="error" header="Andes table deleted">
            The underlying Andes table was deleted. Append is disabled. You can archive this dataset or recreate the Andes table.
          </Alert>
        ) : dataset.andesTableArn ? (
          <KeyValuePairs
            columns={2}
            items={[
              { label: 'Provider', value: dataset.providerName || '-' },
              { label: 'Table', value: dataset.tableName || '-' },
              { label: 'Version', value: (dataset.schemaVersion?.toString()) || '-' },
              { label: 'Link', value: <Link external href={`https://datacentral.a2z.com/hoot/providers/${dataset.providerName}/tables/${dataset.tableName}`}>Open in DataCentral</Link> },
            ]}
          />
        ) : (
          <Box color="text-status-inactive">No Andes table registered for this dataset</Box>
        )}
      </Container>

      {/* Data Preview: first 10 rows from latest snapshot via S3 Select */}
      <Container
        header={
          <Header
            variant="h3"
            description={preview?.sampledFrom?.snapshotDate ? `From snapshot ${preview.sampledFrom.snapshotDate}` : undefined}
          >
            Data preview
          </Header>
        }
      >
        {dataset.status === 'andes_deleted' && (
          <Box color="text-status-inactive">Data preview disabled for datasets with deleted Andes tables.</Box>
        )}
        {dataset.status === 'draft' && (
          <Box color="text-status-inactive">No data yet. Complete the dataset creation first.</Box>
        )}
        {dataset.status !== 'andes_deleted' && dataset.status !== 'draft' && previewLoading && (
          <Box textAlign="center"><Spinner /> <Box display="inline" margin={{ left: 's' }}>Loading preview...</Box></Box>
        )}
        {previewError && <Alert type="warning">Preview unavailable: {previewError}</Alert>}
        {!previewLoading && !previewError && preview && preview.rows.length === 0 && (
          <Box color="text-status-inactive">{preview.message || 'No data yet. Upload a file to see preview.'}</Box>
        )}
        {!previewLoading && !previewError && preview && preview.rows.length > 0 && (
          <Table
            items={preview.rows}
            columnDefinitions={preview.columns.map(col => ({
              id: col,
              header: col,
              cell: (item: Record<string, unknown>) => {
                const v = item[col];
                if (v === null || v === undefined) return '-';
                return typeof v === 'object' ? JSON.stringify(v) : String(v);
              },
            }))}
            variant="embedded"
            empty={<Box>No rows</Box>}
          />
        )}
      </Container>
    </SpaceBetween>
  );
};

export default OverviewTab;
